import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Loader2, Mail, Phone, Star, User as UserIcon } from 'lucide-react';
import { formatCurrency, isPositiveAmount, type VendorWithRelations } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useVendor } from '../../hooks/useVendors';
import { VendorPaymentMethods } from './payment-methods/VendorPaymentMethods';
import type { PaymentTarget } from './payment-methods/PaymentMethodCard';
import { PhoneLink } from '../common/PhoneLink';

export interface VendorReviewModalProps {
  vendorId: string | null;
  open: boolean;
  onClose: () => void;
  /** What is owed to the vendor here — offered as the amount of a pay link. */
  payment?: PaymentTarget & {
    /** Names what the amount is for, e.g. "Open balance on Catering". */
    label?: string;
  };
  /** Add, edit and remove payment methods (PLANNER+). */
  canManagePayments?: boolean;
}

interface ContactRow {
  key: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
}

/** Everybody to reach at the vendor: its own contact, then its contacts. */
function contactRows(vendor: VendorWithRelations): ContactRow[] {
  const rows: ContactRow[] = [];
  // One row per person. Accounts are told apart by id — two may have addresses
  // differing only in case. The vendor's own contact has no account, so a
  // contact at its exact address is taken to be it.
  const seenUsers = new Set<string>();
  const mainEmail = vendor.email?.toLowerCase() ?? null;
  const alreadyListed = (userId: string, email: string | null) =>
    seenUsers.has(userId) || (!!mainEmail && email?.toLowerCase() === mainEmail);

  if (vendor.contactName || vendor.email || vendor.phone) {
    rows.push({
      key: 'vendor',
      name: vendor.contactName || vendor.businessName,
      role: 'Main contact',
      email: vendor.email,
      phone: vendor.phone,
      isPrimary: false,
    });
  }
  for (const c of vendor.contacts) {
    const email = c.userEmail || null;
    if (alreadyListed(c.userId, email)) continue;
    seenUsers.add(c.userId);
    rows.push({
      key: `vc-${c.id}`,
      name: c.userName,
      role: c.role,
      email,
      phone: c.userPhone ?? null,
      isPrimary: c.isPrimary,
    });
  }
  return rows;
}

const linkClass =
  'min-h-11 sm:min-h-0 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 break-all';

/**
 * A vendor at a glance, opened from wherever it is named on an expense: who
 * to reach there, and how to pay it — with the expense's open balance
 * prefilled on any method that pays by link.
 */
export function VendorReviewModal({
  vendorId,
  open,
  onClose,
  payment,
  canManagePayments = false,
}: VendorReviewModalProps) {
  const { data: vendor, isLoading, isError } = useVendor(open ? (vendorId ?? '') : '');
  const contacts = useMemo(() => (vendor ? contactRows(vendor) : []), [vendor]);
  const owed = payment?.amount != null && isPositiveAmount(payment.amount) ? payment.amount : null;

  return (
    <EntityModal
      open={open && !!vendorId}
      onClose={onClose}
      title={vendor?.businessName ?? 'Vendor'}
      size="md"
    >
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 size={24} className="animate-spin text-primary-600" aria-label="Loading" />
        </div>
      ) : isError || !vendor ? (
        <p className="text-sm text-gray-500 py-6 text-center">This vendor could not be loaded.</p>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            {vendor.categories.map((c) => (
              <span
                key={c.id}
                className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded"
              >
                {c.categoryName}
              </span>
            ))}
            {vendor.rating != null && (
              <span className="flex items-center gap-0.5 text-amber-500">
                <Star size={14} className="fill-amber-500" aria-hidden="true" />
                {vendor.rating}
              </span>
            )}
            {vendor.website && (
              <a
                href={
                  vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                <Globe size={14} aria-hidden="true" />
                Website
              </a>
            )}
            <Link to={`/vendors/${vendor.id}`} onClick={onClose} className={`${linkClass} ml-auto`}>
              Open vendor page
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>

          {owed != null && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {payment?.label ?? 'Open balance'}: <strong>{formatCurrency(owed)}</strong>
            </div>
          )}

          <section aria-labelledby="vendor-review-contacts">
            <h3 id="vendor-review-contacts" className="text-sm font-semibold text-gray-900 mb-2">
              Contacts
            </h3>
            {contacts.length === 0 ? (
              <p className="text-sm text-gray-500">No contacts on file.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {contacts.map((c) => (
                  <li
                    key={c.key}
                    className="p-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4"
                  >
                    <div className="flex items-center gap-2 min-w-0 sm:w-1/3">
                      <UserIcon size={14} className="text-gray-400 shrink-0" aria-hidden="true" />
                      <span className="font-medium text-sm text-gray-900 truncate">{c.name}</span>
                      {c.isPrimary && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 rounded">
                          Primary
                        </span>
                      )}
                    </div>
                    {c.role && <span className="text-xs text-gray-500 sm:w-1/6">{c.role}</span>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 sm:ml-auto">
                      {c.email && (
                        <a href={`mailto:${c.email}`} className={linkClass}>
                          <Mail size={14} aria-hidden="true" />
                          {c.email}
                        </a>
                      )}
                      {c.phone && (
                        <PhoneLink
                          phone={c.phone}
                          name={c.name}
                          icon={<Phone size={14} aria-hidden="true" />}
                          className={linkClass}
                        />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="vendor-review-payments">
            <h3 id="vendor-review-payments" className="text-sm font-semibold text-gray-900 mb-2">
              Payment methods
            </h3>
            <VendorPaymentMethods
              vendorId={vendor.id}
              methods={vendor.paymentMethods ?? []}
              canManage={canManagePayments}
              payment={payment}
            />
          </section>
        </div>
      )}
    </EntityModal>
  );
}
